import type { AuthedAxiosRequests } from 'features/auth'
import {
  createDocumentFavoritesApi,
  type DocumentFavoritesEntry,
  type FavoritesApi,
  isFavoritesConflict,
} from 'features/knowledgeDocumentViewer/utils/documentFavoritesApi'
import {
  applyFavoriteOperation,
  type DocumentFavorite,
  emptyDocumentFavorites,
  type FavoriteOperation,
  type FavoritesScope,
} from 'features/knowledgeDocumentViewer/utils/documentFavoritesState'
import { createLegacyFavoritesSource } from 'features/knowledgeDocumentViewer/utils/favoriteDocumentsStorage'

export type LegacyFavorites = {
  read: () => { favorites: DocumentFavorite[]; acknowledge: () => void } | null
}

type FavoritesSnapshot = {
  favorites: DocumentFavorite[]
  isSaving: boolean
  isReady: boolean
}

export const EMPTY_FAVORITES_SNAPSHOT: FavoritesSnapshot = {
  favorites: [],
  isSaving: false,
  isReady: false,
}

const repositories = new Map<
  string,
  {
    repository: DocumentFavoritesRepository
    client: { current: AuthedAxiosRequests }
  }
>()

export function getDocumentFavoritesRepository(
  key: string,
  {
    userId,
    siteId,
    orgId,
    rolodexUrl,
    authorizedAxios,
  }: FavoritesScope & {
    rolodexUrl: string
    authorizedAxios: AuthedAxiosRequests
  }
) {
  const cached = repositories.get(key)
  if (cached) {
    cached.client.current = authorizedAxios
    return cached.repository
  }
  const client = { current: authorizedAxios }
  const repository = createDocumentFavoritesRepository(
    userId,
    createDocumentFavoritesApi(
      rolodexUrl,
      { userId, siteId, orgId },
      () => client.current
    ),
    createLegacyFavoritesSource(userId, siteId)
  )
  repositories.set(key, { repository, client })
  return repository
}

export function createDocumentFavoritesRepository(
  userId: string,
  api: FavoritesApi,
  legacy: LegacyFavorites
) {
  let confirmed: DocumentFavoritesEntry | null | undefined
  let snapshot = EMPTY_FAVORITES_SNAPSHOT
  let queue = Promise.resolve()
  let refreshing: Promise<void> | undefined
  const pending: FavoriteOperation[] = []
  const listeners = new Set<() => void>()

  function publish() {
    const data = pending.reduce(
      applyFavoriteOperation,
      confirmed?.data ?? emptyDocumentFavorites(userId)
    )
    snapshot = {
      favorites: data.favorites,
      isSaving: pending.length > 0,
      isReady: !!confirmed,
    }
    listeners.forEach((listener) => listener())
  }

  function enqueue(task: () => Promise<void>) {
    const result = queue.then(task)
    queue = result.catch(() => {})
    return result
  }

  async function persist(operation: FavoriteOperation) {
    for (let attempt = 0; attempt < 3; attempt++) {
      const data = applyFavoriteOperation(
        confirmed?.data ?? emptyDocumentFavorites(userId),
        operation
      )
      try {
        confirmed = await api.save(data, confirmed?.generation ?? 0)
        return
      } catch (error) {
        // Also reconcile ambiguous network failures: the server may have committed.
        confirmed = await api.load()
        if (!isFavoritesConflict(error) || attempt === 2) throw error
      }
    }
  }

  async function initialize(): Promise<DocumentFavoritesEntry> {
    const imported = legacy.read()
    const data = {
      ...emptyDocumentFavorites(userId),
      favorites: imported?.favorites ?? [],
    }
    for (let attempt = 0; ; attempt++) {
      try {
        const created = await api.save(data, 0)
        imported?.acknowledge()
        return created
      } catch (error) {
        const winner = await api.load()
        if (winner) return winner
        if (!isFavoritesConflict(error) || attempt === 2) throw error
      }
    }
  }

  function refresh() {
    if (refreshing) return refreshing
    refreshing = enqueue(async () => {
      // An empty entry also marks initialization complete; never merge a later browser.
      confirmed = (await api.load()) ?? (await initialize())
    }).finally(() => {
      refreshing = undefined
      publish()
    })
    publish()
    return refreshing
  }

  async function mutate(operation: FavoriteOperation) {
    if (!confirmed) throw new Error('Document favorites are not loaded')
    pending.push(operation)
    publish()
    return enqueue(async () => {
      try {
        await persist(operation)
      } finally {
        pending.splice(pending.indexOf(operation), 1)
        publish()
      }
    })
  }

  return {
    refresh,
    mutate,
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

export type DocumentFavoritesRepository = ReturnType<
  typeof createDocumentFavoritesRepository
>
